<template>
  <div class="full">
    <div class="full media" v-if="path">
      <PlayerMedia
        v-if="showPlayer"
        @error="handleError"
        @notFound="notFound"
        @ended="reset"
        @enableChromecast="enableChromecast"
      />
      <div
        class="full splash"
        v-if="!playing && !showPlayer"
        @click="play"
        :style="{
          backgroundImage: !!path.poster ? 'url(' + path.poster + ')' : 'none'
        }"
      >
        <div class="full splash-button-svg">
          <button
            type="button"
            class="plyr__control plyr__control--overlaid"
            data-plyr="play"
            aria-label="Play"
          >
            <svg aria-hidden="true" focusable="false">
              <svg viewBox="0 0 18 18">
                <path
                  d="M15.562 8.1L3.87.225c-.818-.562-1.87 0-1.87.9v15.75c0 .9 1.052 1.462 1.87.9L15.563 9.9c.584-.45.584-1.35 0-1.8z"
                ></path>
              </svg></svg
            ><span class="plyr__sr-only">Play</span>
          </button>
        </div>
      </div>
      <div
        class="full splash"
        v-if="chromecast && playing"
        @click="disableChromecast"
      >
        <div class="full splash-button-svg">
          <button type="button" class="plyr__control plyr__control--overlaid">
            <svg style="width: 50px; height: 50px;" viewBox="0 0 24 24">
              <path
                d="M1,18 L1,21 L4,21 C4,19.34 2.66,18 1,18 L1,18 Z M1,14 L1,16 C3.76,16 6,18.24 6,21 L8,21 C8,17.13 4.87,14 1,14 L1,14 Z M19,7 L5,7 L5,8.63 C8.96,9.91 12.09,13.04 13.37,17 L19,17 L19,7 L19,7 Z M1,10 L1,12 C5.97,12 10,16.03 10,21 L12,21 C12,14.92 7.07,10 1,10 L1,10 Z M21,3 L3,3 C1.9,3 1,3.9 1,5 L1,8 L3,8 L3,5 L21,5 L21,19 L14,19 L14,21 L21,21 C22.1,21 23,20.1 23,19 L23,5 C23,3.9 22.1,3 21,3 L21,3 Z"
              ></path>
            </svg>
          </button>
        </div>
      </div>
    </div>
    <div class="full overlay error" v-if="error">
      <div class="error-title">{{ error }}</div>
      <p class="error-msg" v-if="errorDetail">{{ errorDetail }}</p>
      <p class="error-msg" v-if="retry > 0">
        Retrying in {{ retry }} seconds...
      </p>
    </div>
    <div class="full overlay" style="opacity: 1;" v-if="loading">
      <p class="error-msg" style="margin-bottom: 1rem;" v-if="loadingDetail">
        {{ loadingDetail }}
      </p>
      <hollow-dots-spinner
        :animation-duration="1000"
        :dot-size="15"
        :dots-num="3"
        color="#ffffff"
      />
    </div>
    <DonateButton v-if="this.donate && !showPlayer" />
  </div>
</template>

<script>
import { mapState, mapMutations } from "vuex";
import PlayerMedia from "@/components/PlayerMedia.vue";
import DonateButton from "@/components/DonateButton.vue";

import { HollowDotsSpinner } from "epic-spinners";

/* global cast, chrome */

export default {
  name: "Player",
  components: {
    PlayerMedia,
    DonateButton,
    HollowDotsSpinner
  },
  props: ["pathUrl"],
  data: function() {
    return {
      playing: false,
      showPlayer: false,
      error: "",
      errorDetail: "",
      interval: null,
      retry: 0,
      loadingDetail: "",
      chromecast: null
    };
  },
  created() {
    this.startInterval();
    this.load();
  },
  beforeDestroy() {
    clearInterval(this.interval);
  },
  computed: {
    ...mapState("player", ["path", "auth", "loading", "donate"]),
    pathFull() {
      const queryString = Object.keys(this.$route.query)
        .map(key => key + "=" + this.$route.query[key])
        .join("&");
      return this.pathUrl + (queryString ? "?" + queryString : "");
    }
  },
  methods: {
    ...mapMutations("player", {
      setLoading: "UPDATE_LOADING",
      setChromecastEnabled: "UPDATE_CHROMECAST_ENABLED"
    }),
    reset() {
      this.clearError();
      this.showPlayer = false;
      this.playing = false;
      this.setLoading(false);
      this.chromecast = null;
    },
    notFound() {
      this.reset();

      if (this.path && this.path.is_live) {
        this.waitRetry("This stream is not currently available.");
      } else {
        this.error = "File Not Found";
        this.errorDetail = "The requested file could not be found.";
      }
    },
    handleError(error) {
      this.reset();
      if (error) {
        this.error = "Error";
        this.errorDetail = error;
      } else {
        this.error = "An unknown error has occurred.";
      }
    },
    waitRetry(msg, timer = 30) {
      this.clearError();
      //this.showPlayer = false;
      this.setLoading(false);
      this.error = msg;
      this.retry = timer;
    },
    loadRetry(msg = "", timer = 30) {
      this.clearError();
      this.setLoading(true);
      this.loadingDetail = msg;
      this.retry = timer;
    },
    startInterval() {
      this.interval = setInterval(() => {
        this.doInterval();
      }, 1000);
    },
    doInterval() {
      if (this.retry > 0) {
        this.retry -= 1;

        if (this.retry <= 0) {
          this.play();
        }
      }
    },
    async play() {
      this.clearError();
      this.setLoading(true);
      await this.$store.dispatch("player/getAuth");
      this.setLoading(false);

      if (this.auth == "VALID") {
        if (this.chromecast) {
          this.doChromecast();
        } else {
          this.playing = true;
          this.showPlayer = true;
        }
      } else if (this.auth == "INVALID") {
        this.error = "Invalid/Expired Link";
      } else if (this.auth == "ACTIVE") {
        if (this.chromecast) {
          this.loadRetry("Authenticating for casting...", 10);
        } else {
          this.waitRetry("Link already in use.");
        }
      }
    },
    async loadPath() {
      this.setLoading(true);
      await this.$store.dispatch("player/getPath", {
        path: this.pathFull,
        ref: document.referrer
      });
      this.setLoading(false);

      if (!this.path) {
        this.notFound();
      }
    },
    clearError() {
      this.error = "";
      this.errorDetail = "";
      this.loadingDetail = "";
    },
    async loadDeps() {
      window["__onGCastApiAvailable"] = isAvailable => {
        this.setChromecastEnabled(isAvailable);

        if (isAvailable) {
          cast.framework.CastContext.getInstance().setOptions({
            receiverApplicationId:
              chrome.cast.media.DEFAULT_MEDIA_RECEIVER_APP_ID
          });

          cast.framework.CastContext.getInstance().addEventListener(
            cast.framework.CastContextEventType.SESSION_STATE_CHANGED,
            data => {
              const ss = cast.framework.SessionState;

              switch (data.sessionState) {
                case ss.NO_SESSION:
                  break;
                case ss.SESSION_STARTING:
                  break;
                case ss.SESSION_STARTED:
                case ss.SESSION_RESUMED:
                  // run on ready
                  break;
                case ss.SESSION_START_FAILED:
                case ss.SESSION_ENDED:
                  if (this.chromecast) {
                    this.reset();
                  }
                  break;
                case ss.SESSION_ENDING:
                  break;
                default:
                  break;
              }

              //console.log(`sessionStateListener: state=${data.sessionState}`);
            }
          );
        }
      };

      await Promise.all([
        this.$loadScript(
          "https://www.gstatic.com/cv/js/sender/v1/cast_sender.js?loadCastFramework=1"
        )
      ]);
    },
    async load() {
      await this.loadDeps();
      await this.loadPath();
    },
    disableChromecast() {
      window.cast.framework.CastContext.getInstance().requestSession();
    },
    enableChromecast(media) {
      const promise = window.cast.framework.CastContext.getInstance().requestSession();
      promise.then(
        () => {
          this.reset();
          this.chromecast = media;
          this.play();
        },
        () => {
          //console.log("onLaunchError", er);
        }
      );
    },
    doChromecast() {
      this.setLoading(true);
      const castSession = window.cast.framework.CastContext.getInstance().getCurrentSession();
      const mediaInfo = new chrome.cast.media.MediaInfo(
        this.chromecast.playlistURL,
        "video/mp4"
      );
      const request = new chrome.cast.media.LoadRequest(mediaInfo);
      castSession.loadMedia(request).then(
        () => {
          this.setLoading(false);
          this.playing = true;
        },
        function(errorCode) {
          console.log("chromecast loadMedia error code: " + errorCode);
        }
      );
    }
  }
};
</script>

<style scoped lang="scss">
.media,
.overlay {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.full {
  width: 100%;
  height: 100%;
}

.splash {
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center center;
}

.splash-button-svg {
  /*
  background: url(~@/assets/images/play_white.png) center no-repeat;
  background-size: 11%;
  */
  cursor: pointer;
}

.splash-button-svg button {
  display: block;
}

.overlay {
  position: absolute;
  background-color: #000;
  color: #fff;
  top: 0;
  left: 0;
  z-index: 1000;
  opacity: 0.9;
}

.error-title {
  font-size: 2rem;
  font-weight: 600;
}
</style>
